import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';

interface DynamicDataTooltipProps {
  data: Record<string, any>;
  children: React.ReactNode;
  excludeFields?: string[];
  fieldLabels?: Record<string, string>;
}

// Helper: Format field values for display
const formatFieldValue = (value: any, fieldName: string): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  // Handle dates
  if (fieldName.toLowerCase().includes('date') || fieldName.toLowerCase().includes('updated')) {
    if (typeof value === 'string' && value.startsWith('0001-01-01')) {
      return '';
    }

    if (typeof value === 'string') {
      try {
        const date = moment(value);

        if (date.isValid()) {
          return date.format('DD MMM YYYY HH:mm:ss');
        }
      } catch {
        return '';
      }
    }
  }

  // Convert boolean to readable text
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Trim strings
  if (typeof value === 'string') {
    return value.trim() === '' ? '' : value.trim();
  }

  return String(value);
};

// Helper: Create readable field label
const createReadableLabel = (fieldName: string): string => {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const DynamicDataTooltip: React.FC<DynamicDataTooltipProps> = ({ data, children, excludeFields = [], fieldLabels = {} }) => {
  const triggerRef = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);

  // NEW: controls actual animation trigger
  const [animateIn, setAnimateIn] = useState(false);

  const [position, setPosition] = useState<null | {
    left: number;
    top: number;
    placement: 'top' | 'bottom';
  }>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();

    const spaceBelow = window.innerHeight - rect.bottom;

    const placement: 'top' | 'bottom' = spaceBelow < 180 ? 'top' : 'bottom';

    setPosition({
      left: rect.left + rect.width / 2,
      top: placement === 'bottom' ? rect.bottom + 12 : rect.top - 12,
      placement,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;

    updatePosition();
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) {
      setAnimateIn(false);
      return;
    }

    // Wait one frame so initial hidden state
    // is painted before transition begins
    const frame = requestAnimationFrame(() => {
      setAnimateIn(true);
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let frame = 0;

    const handleUpdate = () => {
      cancelAnimationFrame(frame);

      frame = requestAnimationFrame(() => {
        updatePosition();
      });
    };

    window.addEventListener('scroll', handleUpdate, true);

    window.addEventListener('resize', handleUpdate);

    return () => {
      cancelAnimationFrame(frame);

      window.removeEventListener('scroll', handleUpdate, true);

      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, updatePosition]);

  const displayableFields = Object.entries(data)
    .filter(([key, value]) => {
      if (excludeFields.includes(key)) return false;

      const formatted = formatFieldValue(value, key);

      return formatted !== '';
    })
    .map(([key, value]) => ({
      key,
      label: fieldLabels[key] || createReadableLabel(key),
      value: formatFieldValue(value, key),
    }));

  if (displayableFields.length === 0) {
    return <>{children}</>;
  }

  const shouldRenderTooltip = isOpen || animateIn;

  const tooltip =
    !position || !shouldRenderTooltip ? null : (
      <div
        className={`
        pointer-events-none
        fixed
        z-[9999]
        flex
        -translate-x-1/2
        transform-gpu
        transition-all duration-200 ease-out
        ${animateIn ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
        ${
          position.placement === 'bottom'
            ? animateIn
              ? 'translate-y-0'
              : '-translate-y-1'
            : animateIn
              ? '-translate-y-full'
              : '-translate-y-[calc(100%+4px)]'
        }
      `}
        style={{
          left: position.left,
          top: position.top,
        }}
      >
        <div
          className="
          relative
          overflow-hidden
          rounded-2xl
          border border-white/10
          bg-black/80
          backdrop-blur-xl
          shadow-[0_10px_40px_rgba(0,0,0,0.45)]
          px-4 py-3
          max-w-[340px]
          min-w-[220px]
        "
        >
          {/* Glow Layer */}
          <div
            className="
            absolute inset-0
            bg-gradient-to-br
            from-white/10
            via-transparent
            to-transparent
            pointer-events-none
          "
          />

          {/* Content */}
          <div className="relative z-10">
            {displayableFields.map((field, idx) => (
              <div
                key={field.key}
                className={`
                mb-2 last:mb-0
                flex items-center justify-between gap-4
                transition-all duration-300
                ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}
              `}
                style={{
                  transitionDelay: `${idx * 40}ms`,
                }}
              >
                <div
                  className="
                  text-[10px]
                  font-semibold
                  uppercase
                  tracking-[0.12em]
                  text-gray-400
                  whitespace-nowrap
                "
                >
                  {field.label}
                </div>

                <div
                  className="
                  text-xs
                  font-medium
                  leading-relaxed
                  text-white
                  break-words
                  text-right
                "
                >
                  {field.value}
                </div>
              </div>
            ))}
          </div>

          {/* Inner Border */}
          <div
            className="
            absolute inset-0
            rounded-2xl
            border border-white/5
          "
          />
        </div>

        {/* Arrow */}
        <div
          className={
            position.placement === 'bottom'
              ? `
              absolute left-1/2 bottom-full
              h-3 w-3
              -translate-x-1/2 translate-y-1/2
              rotate-45
              border-l border-t border-white/10
              bg-black/80
              backdrop-blur-xl
            `
              : `
              absolute left-1/2 top-full
              h-3 w-3
              -translate-x-1/2 -translate-y-1/2
              rotate-45
              border-r border-b border-white/10
              bg-black/80
              backdrop-blur-xl
            `
          }
        />
      </div>
    );

  return (
    <div
      ref={triggerRef}
      className="relative inline-flex cursor-help"
      onMouseEnter={() => {
        setIsOpen(true);
      }}
      onMouseLeave={() => {
        setAnimateIn(false);

        setTimeout(() => {
          setIsOpen(false);
        }, 200);
      }}
      onFocus={() => {
        setIsOpen(true);
      }}
      onBlur={() => {
        setAnimateIn(false);

        setTimeout(() => {
          setIsOpen(false);
        }, 200);
      }}
    >
      {children}

      {typeof document !== 'undefined' ? ReactDOM.createPortal(tooltip, document.body) : null}
    </div>
  );
};

export default DynamicDataTooltip;
